import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("oa_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (name, email, password, date_of_birth, age_confirmed) => {
    const { data } = await api.post("/auth/signup", { name, email, password, date_of_birth, age_confirmed });
    localStorage.setItem("oa_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const verifyAge = useCallback(async (date_of_birth, age_confirmed) => {
    await api.post("/auth/verify-age", { date_of_birth, age_confirmed });
    await checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    localStorage.removeItem("oa_token");
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server logout is best-effort — client is logged out regardless
      console.warn("Server logout failed (client still logged out)", err);
    }
    setUser(null);
  }, []);

  // Memoized context value — prevents unnecessary re-renders of every consumer
  // whenever AuthProvider re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, loading, login, signup, verifyAge, logout, refresh: checkAuth }),
    [user, loading, login, signup, verifyAge, logout, checkAuth]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
